import { useEffect } from "react";
import { useFragment, graphql } from "react-relay/hooks";
import type { ChatPlus_influencer$key as ChatPlusInfluencerKey } from "@generated/ChatPlus_influencer.graphql";
import { chatPlusSiteId } from "@constants/Env";

interface ChatPlusProps {
  influencerFragment: ChatPlusInfluencerKey;
}

interface InfluencerData {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
}

const influencerFragment = graphql`
  fragment ChatPlus_influencer on Influencer {
    id
    email
    firstName
    lastName
    phoneNumber
  }
`;

declare global {
  interface Window {
    __cp_d?: string;
    __cp_c?: string;
    __cp_p?: Record<string, unknown>;
    __cp_f?: Record<string, unknown>;
    chatplus?: {
      open?: () => void;
      show?: () => void;
      maximize?: () => void;
      toggle?: () => void;
    };
    cp?: {
      open?: () => void;
      show?: () => void;
      maximize?: () => void;
      toggle?: () => void;
    };
  }

  interface Document {
    __cp_d?: string;
    __cp_c?: string;
    __cp_p?: Record<string, unknown>;
    __cp_f?: Record<string, unknown>;
  }
}

// RelayのIDをデコードしてデータベースIDを取得する関数
function extractDatabaseId(relayId: string): string {
  try {
    // RelayのIDはBase64エンコードされた "ClassName:DatabaseId" の形式
    const decoded = atob(relayId);
    const parts = decoded.split(":");
    return parts[1] !== undefined && parts[1] !== "" ? parts[1] : relayId; // DatabaseIDを返す、失敗時は元のIDを返す
  } catch {
    return relayId; // デコードに失敗した場合は元のIDを返す
  }
}

export default function ChatPlus({
  influencerFragment: influencerFragmentKey,
}: ChatPlusProps) {
  const influencer = useFragment(
    influencerFragment,
    influencerFragmentKey
  ) as InfluencerData;

  useEffect(() => {
    // ChatPlusのサイトIDが設定されていない場合は何もしない
    if (chatPlusSiteId == null || chatPlusSiteId === "") {
      return undefined;
    }

    // インフルエンサー情報が不完全な場合は待機
    if (influencer == null || influencer.id === "") {
      return undefined;
    }

    const d = document;
    const s = "https://app.chatplus.jp/cp.js";

    // ChatPlusの基本設定
    // eslint-disable-next-line dot-notation
    d["__cp_d"] = "https://app.chatplus.jp";
    // eslint-disable-next-line dot-notation
    d["__cp_c"] = chatPlusSiteId;

    // 訪問者情報の設定
    const firstName = influencer.firstName ?? "";
    const lastName = influencer.lastName ?? "";
    const influencerName = `${lastName} ${firstName}`.trim();
    const databaseId = extractDatabaseId(influencer.id);
    // eslint-disable-next-line dot-notation
    d["__cp_p"] = {
      chatName: influencerName !== "" ? influencerName : "インフルエンサー",
      chatEmail: influencer.email ?? "",
      chatTel: influencer.phoneNumber ?? "",
      externalKey: `influencer_id_${databaseId}`,
    };

    // カスタムフィールド
    // eslint-disable-next-line dot-notation
    d["__cp_f"] = {
      ユーザータイプ: "インフルエンサー",
      氏名: influencerName !== "" ? influencerName : "",
      メールアドレス: influencer.email ?? "",
      電話番号: influencer.phoneNumber ?? "",
      インフルエンサーID: databaseId,
    };

    const a = d.createElement("script");
    const m = d.getElementsByTagName("script")[0];
    a.async = true;
    a.src = s;

    a.onload = () => {
      // スクリプト読み込み完了
    };

    a.onerror = () => {
      // スクリプト読み込みエラー
    };

    if (m.parentNode != null) {
      m.parentNode.insertBefore(a, m);
    }

    // クリーンアップ関数を返す
    return () => {
      // ChatPlusスクリプトを削除
      const scriptTags = Array.from(d.querySelectorAll("script"));
      const scriptsToRemove = scriptTags.filter(
        (script) => script.src !== "" && script.src.includes(s)
      );
      scriptsToRemove.forEach((script) => script.remove());

      // ChatPlusのDOM要素を削除
      const chatplusElements = document.querySelectorAll(
        '[id*="chatplus"], [class*="chatplus"], [id*="cp-"], [class*="cp-"]'
      );
      chatplusElements.forEach((element) => element.remove());

      // ChatPlusのグローバル変数をクリア
      delete window.__cp_d;
      delete window.__cp_c;
      delete window.__cp_p;
      delete window.__cp_f;
      // eslint-disable-next-line dot-notation
      delete d["__cp_d"];
      // eslint-disable-next-line dot-notation
      delete d["__cp_c"];
      // eslint-disable-next-line dot-notation
      delete d["__cp_p"];
      // eslint-disable-next-line dot-notation
      delete d["__cp_f"];
    };
  }, [influencer]);

  // このコンポーネントは何もレンダリングしない（ChatPlusは独自のUIを挿入する）
  return null;
}
